import React from "react";
import { EventProps } from "react-big-calendar";
import styled, { css } from "styled-components";
import AbstractCalendarItem from "@lifeat/shared/calendar/impl/AbstractCalendarItem";
import CalendarTask from "@lifeat/shared/calendar/impl/CalendarTask";
import { userHasTaskTimerAccess } from "@lifeat/shared/user/UserUtil";
import { calculateMinutesBetweenDates, formatDateStandardTime } from "@lifeat/shared/util/DateUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import LifeAtCheckbox from "@common/LifeAtCheckbox/LifeAtCheckbox";
import { useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import AlarmIcon from "@icons/AlarmIcon.svg";
import PlayIcon from "@icons/PlayIcon.svg";
import { FontSize } from "@themes/Fonts";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { CompleteTaskEvent, StartTaskTimerEvent } from "../../../task-management/AmplitudeTaskManagementEvents";
import { useTaskListContext } from "../../../task-management/TaskListContext";
import { startTaskTimer } from "../../../task-management/TaskTimerGlobals";

const Container = styled.div<{ alignCenter: boolean }>`
    display: flex;
    align-items: ${(props) => (props.alignCenter ? `center` : `flex-start`)};

    width: 100%;
    height: 100%;

    padding: 3px;
    gap: 5px;
`;

const EventInfoColumn = styled.div<{ heightFitContent: boolean }>`
    display: flex;
    flex-direction: column;
    overflow: auto;
    height: ${(props) => (props.heightFitContent ? `fit-content` : `100%`)};
    width: 100%;
    gap: 5px;
`;

const EventTitle = styled.div<{ strikeThrough?: boolean }>`
    display: flex;
    gap: 5px;
    align-items: center;
    font-size: ${FontSize.normal};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    ${(props) =>
        props.strikeThrough &&
        css`
            text-decoration: line-through;
        `}
`;

const TagInfoContainer = styled.div`
    display: flex;
    align-items: baseline;
    gap: 5px;
    font-size: ${FontSize.small};
    margin-top: auto;
`;

const TagTitle = styled.span`
    overflow: hidden;
    max-width: 150px;
    white-space: nowrap;
    text-overflow: ellipsis;
    height: 15px;
`;

const TimeText = styled.div`
    font-size: ${FontSize.mini};
    margin-top: auto;
`;

const IconContainer = styled.div`
    display: flex;
    position: relative;
`;

const EventDetailsFooter = styled.div`
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const CheckButton = styled.div`
    height: 16px;
    width: 16px;
    position: absolute;
`;

const TitleRow = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    gap: 5px;
`;

const StyledAlarmIcon = styled(AlarmIcon)`
    scale: 1.2;
`;

const StyledPlayIcon = styled(PlayIcon)<{ isDisabled: boolean }>`
    width: 15px;
    height: 15px;
    opacity: ${(props) => (props.isDisabled ? "0.6" : "1")};
`;

const PlayButton = styled.div`
    height: 15px;
    width: 15px;
    position: absolute;
`;

// Note: The visibility and styling of the checkbox is handled in CalendarEventWrapper.tsx
// This is done so that the checkbox is not visible when the event is being
// dragged or resized, since the position of the checkbox would not be updated
const StyledCheckbox = styled(LifeAtCheckbox)`
    .styled-checkbox {
        visibility: hidden;
    }
    .checkbox-checkmark {
        stroke: none !important;
    }
`;

const EVENT_DURATION_SHOW_TAG_THRESHOLD = 90;
const EVENT_DURATION_SHOW_TIME_THRESHOLD = 60;
const EVENT_DURATION_SINGLE_ROW_THRESHOLD = 30;

function CalendarEventTitle(props: EventProps) {
    const { updateTaskStatus } = useTaskListContext();
    const { user, userData } = useUserContext();
    const { setUpgradeCtaContext } = useCallToActionContext();
    const amplitudeService = AmplitudeService.getInstance();

    const eventResource = props.event.resource as AbstractCalendarItem;

    const eventDurationMins =
        eventResource?.timeStart && eventResource?.timeEnd
            ? calculateMinutesBetweenDates(new Date(eventResource.timeStart), new Date(eventResource.timeEnd))
            : 0;
    const showTagInfo =
        eventResource instanceof CalendarTask &&
        !eventResource?.isAllDayEvent &&
        eventDurationMins >= EVENT_DURATION_SHOW_TAG_THRESHOLD;
    const showTimeInfo = !eventResource?.isAllDayEvent && eventDurationMins >= EVENT_DURATION_SHOW_TIME_THRESHOLD;

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (eventResource instanceof CalendarTask) {
            const updatedStatus = eventResource.status === "OPEN" ? "COMPLETE" : "OPEN";
            updateTaskStatus(eventResource.id, updatedStatus);
            if (updatedStatus) {
                amplitudeService.logEvent(
                    new CompleteTaskEvent({ type: "singular", source: "calendar-view", clientId: eventResource.id }),
                );
            }
        }
    };

    const isTaskTimerDisabled = !userHasTaskTimerAccess(userData, user?.metadata?.creationTime);

    const startTask = () => {
        if (isTaskTimerDisabled) {
            setUpgradeCtaContext("taskTimer", UpgradeCTASource.TASK_TIMER);
        } else {
            startTaskTimer(eventDurationMins, eventResource.summary, () => {
                amplitudeService.logEvent(
                    new StartTaskTimerEvent({
                        source: "calendar-view",
                        taskDuration: eventDurationMins,
                        taskId: eventResource.id,
                    }),
                );
            });
        }
    };

    const startTaskButton = (
        <IconContainer>
            <StyledPlayIcon isDisabled={isTaskTimerDisabled} className={"calendar-task-icon"} />
            <PlayButton
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    startTask();
                }}
            />
        </IconContainer>
    );

    // Note: styling for text and icon color is handled in CalendarEventWrapper.tsx
    return (
        <Container alignCenter={eventDurationMins <= EVENT_DURATION_SINGLE_ROW_THRESHOLD}>
            {eventResource instanceof CalendarTask && eventResource.itemSource === "lifeat-tasks" && (
                <IconContainer>
                    <StyledCheckbox checked={eventResource.status === "COMPLETE"} />
                    <CheckButton onClick={handleCheckboxClick} />
                </IconContainer>
            )}
            {eventResource instanceof CalendarTask &&
                (eventResource.itemSource === "lifeat-task-deadline" ||
                    eventResource.itemSource === "lifeat-project-deadline") && (
                    <StyledAlarmIcon className={"calendar-task-icon"} />
                )}
            <EventInfoColumn heightFitContent={eventDurationMins <= EVENT_DURATION_SINGLE_ROW_THRESHOLD}>
                <TitleRow>
                    <EventTitle
                        strikeThrough={eventResource instanceof CalendarTask && eventResource.status === "COMPLETE"}
                    >
                        {props.event.title}
                    </EventTitle>
                    {eventResource instanceof CalendarTask && startTaskButton}
                </TitleRow>
                {(showTagInfo || showTimeInfo) && (
                    <EventDetailsFooter>
                        {showTagInfo && (
                            <TagInfoContainer>
                                {eventResource?.tagEmoji}
                                <TagTitle>{eventResource?.tagName}</TagTitle>
                            </TagInfoContainer>
                        )}
                        {showTimeInfo && (
                            <TimeText>
                                {formatDateStandardTime(new Date(eventResource?.timeStart))}-
                                {formatDateStandardTime(new Date(eventResource?.timeEnd))}
                            </TimeText>
                        )}
                    </EventDetailsFooter>
                )}
            </EventInfoColumn>
        </Container>
    );
}

export default CalendarEventTitle;
